import { type HTMLAttributes, useEffect, useState } from 'react';
import { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { getRosterFunctions } from 'RestAPIs/roster/functions';
import { MuiAutocompleteOption } from 'components/shared';
import type { RosterFunction } from 'components/types';

const useRosterFunctions = () => {
  'use memo';
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rosterFunctions, setRosterFunctions] = useState<RosterFunction[]>([]);

  const getOptionLabel = (option: RosterFunction | string) => {
    if (typeof option === 'string') {
      return option;
    }

    const description = option?.description ?? '';

    return option?.eFlags?.includes('CAPPLAN_EXCLUDE')
      ? `${description} ${t('excluded').toLowerCase()}`
      : description;
  };

  const renderOption = (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: RosterFunction,
    state: AutocompleteRenderOptionState
  ) => (
    <MuiAutocompleteOption
      {...props}
      key={`option_${option.id}`}
      inputValue={state.inputValue}
      selected={state.selected}
      label={getOptionLabel(option)}
    />
  );

  const isOptionDisabled = (option: RosterFunction) =>
    option.eFlags.includes('CAPPLAN_EXCLUDE');

  const isRosterFunctionsFetched = rosterFunctions.length > 0;

  useEffect(() => {
    if (!isRosterFunctionsFetched) {
      setIsLoading(true);
      getRosterFunctions()
        .then(({ data }) => {
          setRosterFunctions(data);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [isRosterFunctionsFetched]);

  return {
    rosterFunctions,
    isLoading,
    renderOption,
    getOptionLabel,
    isOptionDisabled
  };
};

export default useRosterFunctions;
